import React, { useEffect, useState } from 'react';
import { Translation, getI18n } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import Logger from '../../../../../lib/Logger';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Typography } from 'antd';
import notification from '../../../elements/lib/NotificationWrapper';
import ConfirmationModal from '../../../elements/components/ConfirmationModal';
import LearningAdminModuleSerieCard from './LearningAdminModuleSerieCard';

const { Title } = Typography;

const confirmTexts = {
  title: getI18n().t('learning_admin_module_serie_action_delete_title'),
  description: null,
  leaveText: getI18n().t('learning_admin_module_serie_action_delete_leave'),
  saveText: getI18n().t('learning_admin_module_serie_action_delete_save'),
};

const LearningAdminModuleSeriesGridList = ({
  action,
  partnershipId,
  load,
  patch,
  value,
  defaultLimit,
  defaultOrder,
  remove,
  view,
  activeKey,
  ...props
}) => {
  const [moduleSeries, setModuleSeries] = useState([]);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [moduleSerieIdDelete, setModuleSerieIdDelete] = useState(null);

  useEffect(() => {
    const filter = { [action.split('_not')[0]]: value };

    load(
      partnershipId,
      action,
      filter,
      1,
      defaultLimit,
      defaultOrder,
      (success, result) => {
        if (success) {
          setModuleSeries(result);
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnershipId, action, load]);

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(moduleSeries);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setModuleSeries(items);

    patch(
      partnershipId,
      result.draggableId,
      { order: result.destination.index + 1 },
      (success) => {
        if (!success) {
          const itemsReverted = Array.from(moduleSeries);
          const [revertedItem] = itemsReverted.splice(
            result.destination.index,
            1
          );
          itemsReverted.splice(result.source.index, 0, revertedItem);
          setModuleSeries(itemsReverted);
        }
      }
    );
  };

  const getItemStyle = (isDragging, draggableStyle) => ({
    background: isDragging ? '#E3F2FD' : '#FBFBFB',
    ...draggableStyle,
  });

  const handleDelete = (id) => {
    remove(partnershipId, id, (success) => {
      if (success) {
        const filter = { [action.split('_not')[0]]: value };

        load(
          partnershipId,
          action,
          filter,
          1,
          defaultLimit,
          defaultOrder,
          (success, result) => {
            if (success) {
              setModuleSeries(result);
            }
          }
        );
        notification.success(getI18n().t('feedback_delete_success'));
      } else {
        notification.error(getI18n().t('feedback_delete_error'));
      }
    });
  };

  const handleValue = (id, value) => {
    patch(partnershipId, id, { public_status: value }, (success) => {
      if (success) {
        setModuleSeries((prev) =>
          prev.map((item) =>
            item.id === id ? { ...item, public_status: value } : item
          )
        );
        notification.success(getI18n().t('feedback_update_success'));
      } else {
        notification.error(getI18n().t('feedback_update_error'));
      }
    });
  };

  const handleOpenConfirmationModal = (id) => {
    setConfirmationModalOpen(true);
    setModuleSerieIdDelete(id);
  };

  const handleConfirmLeave = () => {
    setConfirmationModalOpen(false);
    setModuleSerieIdDelete(null);
  };

  const handleConfirmDelete = () => {
    setConfirmationModalOpen(false);
    handleDelete(moduleSerieIdDelete);
    setModuleSerieIdDelete(null);
  };

  return (
    <Translation>
      {(t) => (
        <>
          {moduleSeries.length > 0 && (
            <>
              <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId='module-series-more-content'>
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                      }}
                    >
                      <>
                        <Title
                          level={3}
                          className='learning-title'
                          style={{ marginBottom: '0', marginTop: '20px' }}
                        >
                          {t(`learning_module_serie_${action}`)}
                        </Title>
                        {moduleSeries.map((x, index) => (
                          <Draggable
                            key={x.id}
                            draggableId={x.id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                className='module-serie-card'
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={getItemStyle(
                                  snapshot.isDragging,
                                  provided.draggableProps.style
                                )}
                              >
                                <LearningAdminModuleSerieCard
                                  view={view}
                                  activeKey={activeKey}
                                  item={x}
                                  handleValue={handleValue}
                                  handleOpenConfirmationModal={
                                    handleOpenConfirmationModal
                                  }
                                  partnershipId={partnershipId}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                      </>
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <ConfirmationModal
                texts={confirmTexts}
                openConfirmationModal={confirmationModalOpen}
                onLeaveConfirmation={handleConfirmDelete}
                onSaveConfirmation={handleConfirmLeave}
                onCancel={handleConfirmLeave}
                setConfirmationModalOpen={setConfirmationModalOpen}
              />
            </>
          )}
        </>
      )}
    </Translation>
  );
};

export default withRouter(LearningAdminModuleSeriesGridList);

Logger.log('silly', `LearningAdminModuleSeriesGridList loaded.`);
